import { Component } from '@theme/component';

/**
 * A custom element that manages exclusive accordion behavior.
 *
 * When exclusive mode is enabled via the data-exclusive-accordion attribute,
 * only one accordion row can be open at a time. Opening a new row automatically
 * closes all other rows in the same accordion container.
 *
 * NESTED ACCORDION SUPPORT:
 * Watches for child content size changes (e.g., FAQ accordions opening/closing)
 * and recalculates parent accordion height to prevent overflow/clipping.
 *
 * @extends Component
 */
export class AccordionCustomComponent extends Component {
  #resizeObserver = null;

  /**
   * Called when the element is connected to the document's DOM.
   *
   * Sets up the toggle event listener for handling accordion row interactions
   * and initializes ResizeObserver for nested accordion support.
   */
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('toggle', this.#handleToggle, true);

    // Initialize ResizeObserver for nested accordion height recalculation
    this.#initResizeObserver();
  }

  /**
   * Called when the element is disconnected from the document's DOM.
   *
   * Removes the toggle event listener and cleans up ResizeObserver to prevent memory leaks.
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('toggle', this.#handleToggle, true);

    // Cleanup ResizeObserver
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }
  }

  /**
   * Handles the toggle event on details elements within the accordion.
   *
   * When exclusive mode is enabled and a details element is opened,
   * this method closes all other open details elements within the same
   * accordion container.
   *
   * IMPORTANT: Only processes direct children to prevent nested accordions
   * from affecting their parent accordion rows.
   *
   * @param {ToggleEvent} event - The toggle event from a details element
   */
  #handleToggle = (event) => {
    // Only handle details elements
    if (!(event.target instanceof HTMLDetailsElement)) return;

    // CRITICAL: Only handle details that are direct children of THIS accordion-custom
    // Ignore nested accordion details (they have their own accordion-custom parent)
    // This prevents event bubbling from nested accordions affecting parent accordion rows
    if (event.target.closest('accordion-custom') !== this) return;

    // Only act when opening (not closing)
    if (event.newState !== 'open') return;

    // Check if exclusive mode is enabled on THIS accordion-custom
    if (!this.hasAttribute('data-exclusive-accordion')) return;

    // Find all DIRECT CHILD details elements within THIS accordion-custom only
    // Use :scope > details to only get immediate children, not nested accordions
    const siblings = this.querySelectorAll(':scope > details');

    // Close all siblings except the one that just opened
    siblings.forEach((details) => {
      if (details !== event.target && details.open) {
        details.removeAttribute('open');
      }
    });
  };

  /**
   * Initializes the ResizeObserver for nested accordion support.
   *
   * Watches all .details-content elements for size changes (e.g., when child
   * FAQ accordions open/close) and triggers parent height recalculation.
   */
  #initResizeObserver() {
    // Create ResizeObserver to watch for content size changes
    this.#resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        this.#handleResize(entry);
      });
    });

    // Find all details elements within this accordion
    const detailsElements = this.querySelectorAll('details');

    detailsElements.forEach((details) => {
      // Find the content container within each details element
      const content = details.querySelector('.details-content');

      if (content) {
        // Observe the content container for size changes
        this.#resizeObserver.observe(content);
      }
    });
  }

  /**
   * Handles resize events from the ResizeObserver.
   *
   * When child content resizes (e.g., FAQ accordion opens), ensures the
   * parent details element recalculates its height to prevent clipping.
   *
   * @param {ResizeObserverEntry} entry - The resize observer entry
   */
  #handleResize(entry) {
    // Find the parent details element
    const details = entry.target.closest('details');

    // Only recalculate if the details element is currently open
    if (!details || !details.open) return;

    // Use requestAnimationFrame to batch layout updates
    requestAnimationFrame(() => {
      // Force height recalculation by temporarily setting to auto
      // This ensures smooth expansion when child content grows
      const currentHeight = details.style.height;

      // If height was explicitly set, recalculate it
      if (currentHeight && currentHeight !== 'auto') {
        details.style.height = 'auto';

        // Get the new natural height
        requestAnimationFrame(() => {
          // Browser has now calculated the natural height
          // We can leave it as 'auto' for responsive behavior
        });
      }
    });
  }
}

// Register the custom element
if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustomComponent);
}
